/**
 * Request/collection/project/environment variable scoping — aligned with Collections execution.
 */

export type CollectionTreeNode = {
  id?: number;
  parentCollectionId?: number | null;
};

function isSubCollectionScope(scope?: string | null): boolean {
  const s = String(scope ?? '').toLowerCase().trim().replace(/_/g, '-');
  return s === 'sub-collection';
}

/** True when `descendantId` is a sub-folder under `ancestorId` (not a sibling). */
function isCollectionSubFolderOf(
  descendantId: number,
  ancestorId: number,
  collections: ReadonlyArray<CollectionTreeNode>
): boolean {
  let cur = collections.find((c) => Number(c.id) === descendantId);
  const seen = new Set<number>();
  while (cur?.parentCollectionId != null) {
    const parentId = Number(cur.parentCollectionId);
    if (!Number.isFinite(parentId) || seen.has(parentId)) break;
    seen.add(parentId);
    if (parentId === ancestorId) return true;
    cur = collections.find((c) => Number(c.id) === parentId);
  }
  return false;
}

/** Root collection id for a folder (walk up parentCollectionId). */
export function getCollectionRootId(
  collectionId: number,
  collections: ReadonlyArray<CollectionTreeNode>
): number | null {
  const start = Number(collectionId);
  if (!Number.isFinite(start)) return null;
  let cur = collections.find((c) => Number(c.id) === start);
  let rootId = start;
  const seen = new Set<number>();
  while (cur?.parentCollectionId != null) {
    const parentId = Number(cur.parentCollectionId);
    if (!Number.isFinite(parentId) || seen.has(parentId)) break;
    seen.add(parentId);
    rootId = parentId;
    cur = collections.find((c) => Number(c.id) === parentId);
  }
  return rootId;
}

/**
 * COLLECTION variable is visible anywhere under the same root collection tree
 * (root folder + all nested sub-collections), regardless of which folder declared it.
 * Not visible in other root collections.
 */
export function isCollectionIdAllowedForVariableScope(
  varCollectionId: number,
  requestCollectionId: number | null,
  ancestorCollectionIds?: number[] | null,
  collections?: ReadonlyArray<CollectionTreeNode>
): boolean {
  if (requestCollectionId == null) return false;
  const requestCol = Number(requestCollectionId);
  const declaredCol = Number(varCollectionId);
  if (!Number.isFinite(declaredCol) || !Number.isFinite(requestCol)) return false;
  if (declaredCol === requestCol) return true;
  if (collections?.length) {
    const varRoot = getCollectionRootId(declaredCol, collections);
    const requestRoot = getCollectionRootId(requestCol, collections);
    return varRoot != null && requestRoot != null && varRoot === requestRoot;
  }
  if (ancestorCollectionIds?.some((id) => Number(id) === declaredCol)) return true;
  return false;
}

/**
 * SUB_COLLECTION variable on `varCollectionId` is visible in that folder and nested sub-collections only
 * (not parent folders, siblings, or other branches).
 */
export function isSubCollectionVariableVisible(
  varCollectionId: number,
  requestCollectionId: number | null,
  ancestorCollectionIds?: number[] | null,
  collections?: ReadonlyArray<CollectionTreeNode>
): boolean {
  if (requestCollectionId == null) return false;
  const declaredCol = Number(varCollectionId);
  const requestCol = Number(requestCollectionId);
  if (!Number.isFinite(declaredCol) || !Number.isFinite(requestCol)) return false;
  if (declaredCol === requestCol) return true;
  if (ancestorCollectionIds?.some((id) => Number(id) === declaredCol)) return true;
  if (collections?.length) {
    return isCollectionSubFolderOf(requestCol, declaredCol, collections);
  }
  return false;
}

/** Parent collection ids only (same folder tree). Never siblings or unrelated collections. */
export function resolveAncestorCollectionIdsForVariables(
  collectionId: number | null,
  getAncestorIds: (id: number) => number[]
): number[] {
  if (collectionId == null) return [];
  return getAncestorIds(collectionId);
}

export function runtimeVariableScopePriority(scope?: string | null): number {
  // REQUEST > SUB_COLLECTION > COLLECTION > ENVIRONMENT > PROJECT
  const s = String(scope ?? '').toLowerCase().trim().replace(/_/g, '-');
  if (s === 'request') return 5;
  if (s === 'sub-collection') return 4;
  if (s === 'collection') return 3;
  if (s === 'environment') return 2;
  if (s === 'project') return 1;
  return 0;
}

export function isUnresolvedRuntimeVariableValue(value: unknown): boolean {
  const s = value != null ? String(value).trim() : '';
  if (s === '' || s.includes('{{')) return true;
  if (/^\{[\w]+\}$/.test(s) || /^:[\w]+$/.test(s)) return true;
  return false;
}

/**
 * Environment API / .env overlay may fill project-scoped gaps only.
 * Request and collection scoped variables always win over environment (even when empty).
 */
export function canEnvironmentOverlayReplaceScopedVariable(
  existing: { scope?: string | null; value?: unknown } | undefined
): boolean {
  if (!existing) return true;
  if (
    runtimeVariableScopePriority(existing.scope) >
    runtimeVariableScopePriority('environment')
  ) {
    return false;
  }
  return isUnresolvedRuntimeVariableValue(existing.value);
}

/** True when env fallback is allowed for a named runtime variable (path/url replacers). */
export function canUseEnvironmentValueForVariableName(
  vars: Array<{ name?: string; scope?: string | null; value?: unknown }>,
  paramName: string
): boolean {
  const key = String(paramName ?? '').trim();
  if (!key) return true;
  const existing = vars.find((v) => String(v?.name ?? '').trim() === key);
  if (!existing) return true;
  if (
    runtimeVariableScopePriority(existing.scope) >
    runtimeVariableScopePriority('environment')
  ) {
    return false;
  }
  return isUnresolvedRuntimeVariableValue(existing.value);
}

/** Drop variables whose scope does not match the active request/collection/project/environment. */
export function filterVariablesByContextIds(
  input: any[] | null | undefined,
  ctx: {
    requestId?: number | null;
    collectionId?: number | null;
    ancestorCollectionIds?: number[] | null;
    projectId?: number | null;
    environmentId?: number | null;
    collections?: ReadonlyArray<CollectionTreeNode>;
  }
): any[] {
  if (!Array.isArray(input)) return [];
  const activeRequestId = ctx.requestId ?? null;
  const activeCollectionId = ctx.collectionId ?? null;
  const activeProjectId = ctx.projectId ?? null;
  const activeEnvironmentId = ctx.environmentId ?? null;

  return input.filter((v: any) => {
    const varCollectionId =
      v?.collectionId != null && Number.isFinite(Number(v.collectionId))
        ? Number(v.collectionId)
        : null;
    const scope = String(v?.scope ?? '').toLowerCase().trim();
    if (varCollectionId != null && activeCollectionId != null) {
      if (isSubCollectionScope(scope)) {
        if (
          !isSubCollectionVariableVisible(
            varCollectionId,
            activeCollectionId,
            ctx.ancestorCollectionIds,
            ctx.collections
          )
        ) {
          return false;
        }
      } else if (
        !isCollectionIdAllowedForVariableScope(
          varCollectionId,
          activeCollectionId,
          ctx.ancestorCollectionIds,
          ctx.collections
        )
      ) {
        return false;
      }
    }
    if (!scope) {
      if (v?.requestId != null) {
        return activeRequestId != null && Number(v.requestId) === Number(activeRequestId);
      }
      if (v?.collectionId != null) {
        return isCollectionIdAllowedForVariableScope(
          Number(v.collectionId),
          activeCollectionId,
          ctx.ancestorCollectionIds,
          ctx.collections
        );
      }
      if (v?.environmentId != null) {
        return activeEnvironmentId != null && Number(v.environmentId) === Number(activeEnvironmentId);
      }
      if (v?.projectId != null) {
        return activeProjectId != null && Number(v.projectId) === Number(activeProjectId);
      }
      return false;
    }
    if (scope === 'request') {
      if (activeRequestId == null) return false;
      if (v?.requestId == null) return false;
      return Number(v.requestId) === Number(activeRequestId);
    }
    if (scope === 'collection') {
      if (activeCollectionId == null) return false;
      if (v?.collectionId == null) return false;
      return isCollectionIdAllowedForVariableScope(
        Number(v.collectionId),
        activeCollectionId,
        ctx.ancestorCollectionIds,
        ctx.collections
      );
    }
    if (isSubCollectionScope(scope)) {
      if (activeCollectionId == null) return false;
      if (v?.collectionId == null) return false;
      return isSubCollectionVariableVisible(
        Number(v.collectionId),
        activeCollectionId,
        ctx.ancestorCollectionIds,
        ctx.collections
      );
    }
    if (scope === 'environment') {
      if (activeEnvironmentId == null) return false;
      if (v?.environmentId == null) return false;
      return Number(v.environmentId) === Number(activeEnvironmentId);
    }
    if (scope === 'project') {
      if (activeProjectId == null) return false;
      if (v?.projectId == null) return false;
      return Number(v.projectId) === Number(activeProjectId);
    }
    return false;
  });
}

/** Collection id for variable scope — from the request DTO, not UI selection. */
export function collectionIdFromRequestLike(
  req: { collectionId?: number | null; collection?: { id?: number | null } | null } | null | undefined,
  fallbackCollectionId?: number | null
): number | null {
  const raw = req
    ? (req as any).collectionId ?? (req as any).collection?.id ?? null
    : null;
  if (raw != null) {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  if (fallbackCollectionId != null) {
    const n = Number(fallbackCollectionId);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/**
 * GET /environments/:id/variables returns env + project + collection + request rows for the
 * Environments page. Only environment (+ project for merge) belong in runtime substitution lists.
 */
export function isEnvironmentApiRowForSubstitution(ev: {
  scope?: string | null;
  collectionId?: number | null;
  requestId?: number | null;
}): boolean {
  const scope = String(ev?.scope ?? '').toLowerCase().trim();
  if (scope === 'collection' || scope === 'request') return false;
  if (scope === 'environment' || scope === 'project') return true;
  if (ev?.collectionId != null) return false;
  if (ev?.requestId != null) return false;
  return scope === '';
}

/** COLLECTION / SUB_COLLECTION rows only when visible in the active folder tree. */
export function denyForeignCollectionVariables(
  input: any[] | null | undefined,
  activeCollectionId: number | null,
  ancestorCollectionIds?: number[] | null,
  collections?: ReadonlyArray<CollectionTreeNode>
): any[] {
  if (!Array.isArray(input)) return [];
  const allowed = new Set<number>();
  if (activeCollectionId != null && Number.isFinite(Number(activeCollectionId))) {
    allowed.add(Number(activeCollectionId));
  }
  for (const id of ancestorCollectionIds ?? []) {
    if (id != null && Number.isFinite(Number(id))) allowed.add(Number(id));
  }
  return input.filter((v: any) => {
    const scope = String(v?.scope ?? '').toLowerCase().trim();
    const vCol =
      v?.collectionId != null && Number.isFinite(Number(v.collectionId))
        ? Number(v.collectionId)
        : null;
    if (isSubCollectionScope(scope)) {
      if (vCol == null || activeCollectionId == null) return false;
      return isSubCollectionVariableVisible(
        vCol,
        activeCollectionId,
        ancestorCollectionIds,
        collections
      );
    }
    if (scope === 'collection') {
      if (vCol == null || activeCollectionId == null) return false;
      return isCollectionIdAllowedForVariableScope(
        vCol,
        activeCollectionId,
        ancestorCollectionIds,
        collections
      );
    }
    if (scope !== 'collection' && vCol == null) return true;
    if (vCol == null) return scope !== 'collection';
    if (allowed.size === 0) return false;
    return allowed.has(vCol);
  });
}

export type EnvironmentPageWireVariable = {
  scope?: string | null;
  projectId?: number | null;
  environmentId?: number | null;
  collectionId?: number | null;
  requestId?: number | null;
};

const ENVIRONMENT_PAGE_WIRE_SCOPES = [
  'request',
  'environment',
  'collection',
  'sub-collection',
  'project',
] as const;

export type EnvironmentPageWireScope = (typeof ENVIRONMENT_PAGE_WIRE_SCOPES)[number];

/** Normalize scope from GET /environments/:id/variables wire DTOs. */
export function normalizeEnvironmentPageWireScope(scope?: string | null): EnvironmentPageWireScope | null {
  const raw = String(scope ?? '')
    .toLowerCase()
    .trim()
    .replace(/_/g, '-');
  if (!raw) return null;
  return (ENVIRONMENT_PAGE_WIRE_SCOPES as readonly string[]).includes(raw)
    ? (raw as EnvironmentPageWireScope)
    : null;
}

/** Infer scope from explicit wire scope or relation ids (never collapse collection → environment). */
export function inferEnvironmentPageWireScope(
  ev: EnvironmentPageWireVariable
): EnvironmentPageWireScope | null {
  const explicit = normalizeEnvironmentPageWireScope(ev.scope);
  if (explicit) return explicit;
  if (ev.requestId != null) return 'request';
  if (ev.collectionId != null) return 'collection';
  if (ev.environmentId != null) return 'environment';
  if (ev.projectId != null) return 'project';
  return null;
}

/** Inline Environments grid only supports environment/project rows. */
export function toEnvironmentInlineRowScope(
  ev: EnvironmentPageWireVariable
): 'environment' | 'project' {
  return inferEnvironmentPageWireScope(ev) === 'project' ? 'project' : 'environment';
}

export function getCollectionAncestorIdsFromTree(
  collectionId: number,
  collections: ReadonlyArray<CollectionTreeNode>
): number[] {
  const ancestors: number[] = [];
  let cur = collections.find((c) => Number(c.id) === collectionId);
  const seen = new Set<number>();
  while (cur?.parentCollectionId != null) {
    const parentId = Number(cur.parentCollectionId);
    if (!Number.isFinite(parentId) || seen.has(parentId)) break;
    seen.add(parentId);
    ancestors.push(parentId);
    cur = collections.find((c) => Number(c.id) === parentId);
  }
  return ancestors;
}
